import { useMemo } from 'react';
import Plot from 'react-plotly.js';
import type { Data, Layout } from 'plotly.js';
import type { TestChartData } from '@/lib/impedance';
import { radToDeg } from '@/lib/impedance';
import { formatLegendLabel } from './BodePlot';

export interface DeltaPlotProps {
  /** Pre-computed delta data — baseline field holds delta channels, specimen is null */
  testData: TestChartData[];
  visibleChannels: Set<string>;
  phaseUnit: 'rad' | 'deg';
  colorMap: Map<string, string>;
  deltaMode: 'bode' | 'nyquist';
  onPlotRef?: (el: HTMLElement | null) => void;
}

export function DeltaPlot({
  testData,
  visibleChannels,
  phaseUnit,
  colorMap,
  deltaMode,
  onPlotRef,
}: DeltaPlotProps) {
  const traces = useMemo<Data[]>(() => {
    const result: Data[] = [];
    const useWebGL = testData.length > 10;
    const traceType = useWebGL ? 'scattergl' : 'scatter';

    if (deltaMode === 'bode') {
      // Zero-reference lines at y=0 for both magnitude and phase axes
      const refFreqs = testData[0]?.baseline?.frequencies;
      if (refFreqs && refFreqs.length > 0) {
        const zeros = refFreqs.map(() => 0);
        result.push({
          x: refFreqs,
          y: zeros,
          type: 'scatter',
          mode: 'lines',
          name: 'Zero reference',
          xaxis: 'x2',
          yaxis: 'y',
          line: { color: 'rgba(128,128,128,0.5)', dash: 'dash', width: 1 },
          showlegend: false,
          hoverinfo: 'skip',
        } as Data);
        result.push({
          x: refFreqs,
          y: zeros,
          type: 'scatter',
          mode: 'lines',
          name: 'Zero reference φ',
          xaxis: 'x',
          yaxis: 'y2',
          line: { color: 'rgba(128,128,128,0.5)', dash: 'dash', width: 1 },
          showlegend: false,
          hoverinfo: 'skip',
        } as Data);
      }

      for (const test of testData) {
        if (!test.baseline) continue;
        const color = colorMap.get(test.testId) ?? '#888';

        for (const channel of test.baseline.channels) {
          if (!visibleChannels.has(channel.channelId)) continue;

          const isFirstTraceForTest = !result.some(
            (t) => (t as any).legendgroup === test.testId,
          );
          const baseLabel = `${test.label} ${channel.channelId} Δ`;
          const label = formatLegendLabel(baseLabel, test.notes);
          const notesLine = test.notes ? `Note: ${test.notes}<br>` : '';

          // ΔMagnitude trace (top panel → xaxis2)
          result.push({
            x: channel.points.map((p) => p.frequency),
            y: channel.points.map((p) => p.magnitude),
            type: traceType as 'scatter' | 'scattergl',
            mode: 'lines+markers',
            name: label,
            xaxis: 'x2',
            yaxis: 'y',
            line: { color, width: 1.5 },
            marker: { size: 3 },
            legendgroup: test.testId,
            legendgrouptitle: isFirstTraceForTest ? { text: test.label } : undefined,
            hovertemplate:
              `<b>${baseLabel}</b><br>` +
              notesLine +
              `Freq: %{x:.2f} Hz<br>` +
              `ΔMag: %{y:.1f} Ω<br>` +
              `<extra></extra>`,
            showlegend: true,
          } as Data);

          // ΔPhase trace (y2)
          const phaseValues =
            phaseUnit === 'deg'
              ? channel.points.map((p) => radToDeg(p.phase))
              : channel.points.map((p) => p.phase);

          result.push({
            x: channel.points.map((p) => p.frequency),
            y: phaseValues,
            type: traceType as 'scatter' | 'scattergl',
            mode: 'lines',
            name: `${label} φ`,
            yaxis: 'y2',
            line: { color, dash: 'dot', width: 1 },
            legendgroup: test.testId,
            hovertemplate:
              `<b>${baseLabel} ΔPhase</b><br>` +
              notesLine +
              `Freq: %{x:.2f} Hz<br>` +
              `ΔPhase: %{y:.3f} ${phaseUnit === 'deg' ? '°' : 'rad'}<br>` +
              `<extra></extra>`,
            showlegend: false,
          } as Data);
        }
      }
    } else {
      // Nyquist delta mode — ΔZ_real vs -ΔZ_imag
      // Cross marker at origin as the "no change" reference
      result.push({
        x: [0],
        y: [0],
        type: 'scatter',
        mode: 'markers',
        name: 'Zero reference',
        marker: {
          symbol: 'cross',
          size: 14,
          color: 'rgba(128,128,128,0.6)',
          line: { width: 2, color: 'rgba(128,128,128,0.6)' },
        },
        showlegend: false,
        hoverinfo: 'skip',
      } as Data);

      for (const test of testData) {
        if (!test.baseline) continue;
        const color = colorMap.get(test.testId) ?? '#888';

        for (const channel of test.baseline.channels) {
          if (!visibleChannels.has(channel.channelId)) continue;

          const isFirstTraceForTest = !result.some(
            (t) => (t as any).legendgroup === test.testId,
          );
          const baseLabel = `${test.label} ${channel.channelId} Δ`;
          const label = formatLegendLabel(baseLabel, test.notes);
          const notesLine = test.notes ? `Note: ${test.notes}<br>` : '';

          result.push({
            x: channel.points.map((p) => p.zReal),
            y: channel.points.map((p) => -p.zImag),
            customdata: channel.points.map((p) => p.frequency),
            type: traceType as 'scatter' | 'scattergl',
            mode: 'lines+markers',
            name: label,
            line: { color, width: 1.5 },
            marker: { size: 4, color },
            legendgroup: test.testId,
            legendgrouptitle: isFirstTraceForTest ? { text: test.label } : undefined,
            hovertemplate:
              `<b>${baseLabel}</b><br>` +
              notesLine +
              `ΔZ_real: %{x:.1f} Ω<br>` +
              `−ΔZ_imag: %{y:.1f} Ω<br>` +
              `Freq: %{customdata:.2f} Hz<br>` +
              `<extra></extra>`,
            showlegend: true,
          } as Data);
        }
      }
    }

    return result;
  }, [testData, visibleChannels, phaseUnit, colorMap, deltaMode]);

  const layout = useMemo<Partial<Layout>>(() => {
    if (deltaMode === 'bode') {
      return {
        // Stacked subplots: xaxis2 (top/magnitude, no labels) linked to xaxis (bottom/phase, master)
        xaxis: {
          title: { text: 'Frequency (Hz)' },
          type: 'log',
          autorange: true,
          anchor: 'y2',
        },
        xaxis2: {
          type: 'log',
          matches: 'x',
          showticklabels: false,
          anchor: 'y',
        } as Partial<Layout>['xaxis'],
        yaxis: {
          title: { text: 'ΔMagnitude (Ω)' },
          autorange: true,
          domain: [0.55, 1.0],
          anchor: 'x2',
        },
        yaxis2: {
          title: { text: `ΔPhase (${phaseUnit === 'deg' ? '°' : 'rad'})` },
          autorange: true,
          domain: [0.0, 0.45],
          anchor: 'x',
        },
        legend: {
          orientation: 'h',
          y: -0.22,
          x: 0,
          groupclick: 'toggleitem',
        },
        hovermode: 'closest',
        margin: { t: 30, r: 20, b: 120, l: 60 },
        autosize: true,
      };
    }
    // Nyquist delta
    return {
      xaxis: {
        title: { text: 'ΔZ_real (Ω)' },
        scaleanchor: 'y',
        scaleratio: 1,
        autorange: true,
      },
      yaxis: {
        title: { text: '−ΔZ_imag (Ω)' },
        autorange: true,
      },
      legend: {
        orientation: 'h',
        y: -0.25,
        x: 0,
        groupclick: 'toggleitem',
      },
      hovermode: 'closest',
      margin: { t: 30, r: 60, b: 120, l: 60 },
      autosize: true,
    };
  }, [deltaMode, phaseUnit]);

  if (testData.length === 0) {
    return null;
  }

  return (
    <Plot
      data={traces}
      layout={layout}
      style={{ width: '100%', height: '100%' }}
      useResizeHandler
      config={{ responsive: true, displayModeBar: true, displaylogo: false, scrollZoom: true }}
      onInitialized={(_, graphDiv) => onPlotRef?.(graphDiv)}
      onPurge={() => onPlotRef?.(null)}
    />
  );
}
